import { useEffect, useRef, useState, forwardRef } from "react";
import { loadGoogleMaps, parsePlace } from "../../utils/googleMapsLoader";

// Drop-in replacement for any single-line address <input> across the app.
// Wraps Google Places Autocomplete: as the user types, a dropdown of
// matching addresses appears. Selecting one fires `onPlaceSelected` with a
// flat shape — { address, address2, city, state, zip, country, formatted,
// lat, lng } — that the parent component plugs into its own field state
// (Office Address / Office City / Office State / Office Zip etc.).
//
// Graceful degradation: if REACT_APP_GOOGLE_MAPS_API_KEY is missing or the
// script fails to load, the component renders as a plain text input. No
// crash, no missing functionality beyond the autocomplete suggestions.
//
// Props:
//   value           — controlled string value (the visible text in the input)
//   onChange        — fires on every keystroke; signature: (newValue: string)
//   onPlaceSelected — fires when the user picks a suggestion;
//                     signature: (parsed: Address) — see parsePlace()
//   country         — restrict suggestions to one or more ISO country codes
//                     (defaults to ["us"]). Pass null/undefined to disable.
//   types           — Places API "types" param (defaults to ["address"]).
//                     Pass "establishment" for businesses, "(regions)" for
//                     city/state-only, etc.
//   placeholder     — input placeholder
//   className       — passed through to the underlying <input>
//   inputProps      — any other input props (name, autoFocus, disabled, …)
//
// The ref (when forwarded) points at the underlying <input> so callers can
// focus()/blur() it programmatically.
const AddressAutocomplete = forwardRef(function AddressAutocomplete(
  { value = "", onChange, onPlaceSelected, country = ["us"], types = ["address"], placeholder, className, inputProps = {} },
  forwardedRef
) {
  const localRef = useRef(null);
  const inputRef = forwardedRef || localRef;
  const autocompleteRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps().then(({ available, google }) => {
      if (cancelled || !available || !inputRef.current) return;
      const ac = new google.maps.places.Autocomplete(inputRef.current, {
        types: Array.isArray(types) ? types : [types],
        fields: ["address_components", "formatted_address", "geometry", "name"],
        ...(country ? { componentRestrictions: { country: Array.isArray(country) ? country : [country] } } : {}),
      });
      ac.addListener("place_changed", () => {
        const place = ac.getPlace();
        const parsed = parsePlace(place);
        // Reflect the formatted address into the visible input so a
        // controlled parent value stays in sync — and so React doesn't
        // overwrite the picked text on next render with a stale value.
        if (parsed && onChange) onChange(parsed.formatted || place.name || "");
        if (parsed && onPlaceSelected) onPlaceSelected(parsed);
      });
      autocompleteRef.current = ac;
      setReady(true);
    });
    return () => {
      cancelled = true;
      if (autocompleteRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
      autocompleteRef.current = null;
    };
    // country / types are usually stable; if they change at runtime the
    // user can re-mount the component. Keeping the dep array narrow avoids
    // tearing down and rebuilding the Autocomplete on every parent rerender.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Google injects a .pac-container popup at document.body; nothing to do
  // here for that — but we DO need to keep our controlled value in sync
  // with the input on each render.
  return (
    <input
      ref={inputRef}
      type="text"
      value={value}
      onChange={(e) => onChange && onChange(e.target.value)}
      placeholder={placeholder}
      className={className}
      autoComplete="off"
      data-google-places-ready={ready ? "true" : "false"}
      {...inputProps}
    />
  );
});

export default AddressAutocomplete;
